import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import Header from "@/components/Header";
import ProductCard, { formatPrice } from "@/components/ProductCard";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";
import {
  Package, Receipt, User as UserIcon, Truck, CreditCard, CaretDown,
  Heart, ChatCircleDots, Star, MapPin, Plus, Trash, ArrowClockwise, SealCheck, CheckCircle, MapPinLine, Warning,
} from "@phosphor-icons/react";
import { ClaimsTab, NewClaimModal } from "@/components/ClaimsCustomer";
import InvoiceModal from "@/components/InvoiceModal";
import AuthScreen from "@/components/AuthScreen";

const PLACEHOLDER = "https://images.unsplash.com/photo-1495100497150-fe209c585f50?crop=entropy&cs=srgb&fm=jpg&q=85&w=800";

const ORDER_STATUS = {
  paid: { label: "Pagado", cls: "bg-[hsl(var(--meli-green))]/15 text-[hsl(var(--meli-green))]" },
  pending_transfer: { label: "Pendiente de verificación", cls: "bg-amber-100 text-amber-700" },
  rejected: { label: "Rechazado", cls: "bg-red-100 text-red-600" },
  pending: { label: "Pago pendiente", cls: "bg-gray-100 text-gray-600" },
};

const TRACK_STAGES = [
  { key: "Preparando producto", icon: Package },
  { key: "Enviado", icon: Truck },
  { key: "En camino", icon: MapPinLine },
  { key: "Entregado", icon: CheckCircle },
];

function TrackingTimeline({ order, onRefresh }) {
  if (order.shipping_status === "Cancelado") {
    return <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-600" data-testid={`tracking-${order.id}`}>Envío cancelado</div>;
  }
  let cur = TRACK_STAGES.findIndex((s) => s.key === order.shipping_status);
  if (cur < 0) cur = 0;
  const lastEvent = (order.tracking_events || [])[0];
  return (
    <div className="rounded-lg border border-black/10 p-4" data-testid={`tracking-${order.id}`}>
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Seguimiento del envío</p>
        <div className="flex items-center gap-2">
          {order.tracking_number && <p className="text-xs text-foreground/80">{order.carrier ? <b>{order.carrier}</b> : null} · Guía {order.tracking_number}</p>}
          {order.tracking_number && <button onClick={onRefresh} data-testid={`refresh-track-${order.id}`} className="text-[11px] text-primary hover:underline flex items-center gap-0.5"><ArrowClockwise weight="bold" size={12} /> Actualizar</button>}
        </div>
      </div>
      <div className="flex items-start">
        {TRACK_STAGES.map((s, i) => {
          const Icon = s.icon;
          const done = i <= cur;
          return (
            <React.Fragment key={s.key}>
              <div className="flex flex-col items-center flex-1 min-w-0">
                <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${done ? "bg-[hsl(var(--meli-green))] text-white" : "bg-secondary text-muted-foreground"}`}>
                  <Icon weight={done ? "fill" : "regular"} size={18} />
                </div>
                <span className={`text-[10px] sm:text-xs mt-1.5 text-center leading-tight ${i === cur ? "font-bold text-foreground" : "text-muted-foreground"}`}>{s.key}</span>
              </div>
              {i < TRACK_STAGES.length - 1 && <div className={`h-1 flex-1 mt-4 rounded ${i < cur ? "bg-[hsl(var(--meli-green))]" : "bg-secondary"}`} style={{ minWidth: 20 }} />}
            </React.Fragment>
          );
        })}
      </div>
      {(lastEvent || order.track_url) && (
        <div className="mt-3 pt-3 border-t border-black/5 flex items-center justify-between gap-2 flex-wrap">
          {lastEvent ? <p className="text-[11px] text-muted-foreground">{lastEvent.description}{lastEvent.date ? ` · ${lastEvent.date}` : ""}</p> : <span />}
          {order.track_url && <a href={order.track_url} target="_blank" rel="noreferrer" data-testid={`envia-link-${order.id}`} className="text-[11px] text-primary hover:underline shrink-0">Ver rastreo en Envia →</a>}
        </div>
      )}
    </div>
  );
}

function Stars({ value = 0, size = 14 }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} weight={i <= Math.round(value) ? "fill" : "regular"} size={size}
              className={i <= Math.round(value) ? "text-primary" : "text-gray-300"} />
      ))}
    </div>
  );
}

const TABS = [
  ["compras", "Mis compras", Receipt],
  ["reclamos", "Mis reclamos", Warning],
  ["preguntas", "Mis preguntas", ChatCircleDots],
  ["opiniones", "Mis opiniones", Star],
  ["favoritos", "Mis favoritos", Heart],
  ["datos", "Mis datos", UserIcon],
];

export default function Account() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const validTabs = ["compras", "reclamos", "preguntas", "opiniones", "favoritos", "datos"];
  const initialTab = validTabs.includes(searchParams.get("tab")) ? searchParams.get("tab") : "compras";
  const [tab, setTab] = useState(initialTab);

  if (!user) {
    return <AuthScreen />;
  }

  return (
    <div className="App min-h-screen bg-background">
      <Header />
      <div className="max-w-[1000px] mx-auto px-3 sm:px-5 py-6">
        <div className="bg-white rounded-lg nb-shadow p-5 flex items-center gap-4" data-testid="account-profile">
          {user.picture ? <img src={user.picture} alt="" className="w-14 h-14 rounded-full object-cover" /> : <span className="w-14 h-14 rounded-full bg-secondary flex items-center justify-center"><UserIcon weight="bold" size={24} /></span>}
          <div>
            <h1 className="text-xl font-extrabold tracking-tight">{user.name}</h1>
            <p className="text-sm text-muted-foreground">{user.email}</p>
          </div>
        </div>

        <div className="flex gap-1 mt-5 overflow-x-auto no-scrollbar border-b border-black/10">
          {TABS.map(([key, label, Icon]) => (
            <button key={key} onClick={() => setTab(key)} data-testid={`account-tab-${key}`}
                    className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-semibold whitespace-nowrap border-b-2 transition-colors ${tab === key ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
              <Icon weight="bold" size={16} /> {label}
            </button>
          ))}
        </div>

        <div className="mt-5">
          {tab === "compras" && <PurchasesTab goToClaims={() => setTab("reclamos")} />}
          {tab === "reclamos" && <ClaimsTab />}
          {tab === "preguntas" && <QuestionsTab />}
          {tab === "opiniones" && <ReviewsTab />}
          {tab === "favoritos" && <FavoritesTab />}
          {tab === "datos" && <ProfileTab />}
        </div>
      </div>
    </div>
  );
}

function EmptyState({ icon: Icon, title, cta }) {
  return (
    <div className="bg-white rounded-lg nb-shadow p-10 text-center">
      <Icon weight="bold" size={36} className="mx-auto text-muted-foreground" />
      <p className="text-lg font-bold mt-3">{title}</p>
      {cta}
    </div>
  );
}

function PurchasesTab({ goToClaims }) {
  const { add } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);
  const [claimOrder, setClaimOrder] = useState(null);
  const [claimByOrder, setClaimByOrder] = useState({});
  const [invoiceOrder, setInvoiceOrder] = useState(null);

  const loadOrders = () => api.get("/buyer/orders").then((r) => setOrders(r.data)).finally(() => setLoading(false));
  const loadClaims = () => api.get("/buyer/claims").then((r) => {
    const map = {};
    r.data.forEach((c) => {
      const active = !["cerrado", "cerrado_sin_respuesta"].includes(c.status);
      if (active || !map[c.order_id]) map[c.order_id] = { folio: c.folio, active };
    });
    setClaimByOrder(map);
  }).catch(() => {});

  useEffect(() => { loadOrders(); loadClaims(); }, []);

  const reorder = (o) => {
    o.items.forEach((it) => add(it.product_id, it.variant_id || null, it.quantity));
    toast.success("Productos agregados al carrito");
    navigate("/carrito");
  };

  const syncTrack = async (o) => {
    if (!o.tracking_number) return;
    try {
      const r = await api.post(`/orders/${o.id}/track-sync`);
      setOrders((list) => list.map((x) => (x.id === o.id ? r.data : x)));
    } catch {}
  };

  const toggleOpen = (o) => {
    const nv = open === o.id ? null : o.id;
    setOpen(nv);
    if (nv && o.status === "paid") syncTrack(o);
  };

  if (loading) return <p className="text-muted-foreground">Cargando…</p>;
  if (orders.length === 0) return <EmptyState icon={Package} title="Todavía no realizaste compras" cta={<Link to="/" className="mt-4 inline-block nb-btn bg-primary text-primary-foreground px-6 py-3 hover:brightness-110">Ir a comprar</Link>} />;

  return (
    <div className="space-y-3" data-testid="orders-list">
      {orders.map((o) => (
        <div key={o.id} className="bg-white rounded-lg nb-shadow overflow-hidden" data-testid={`order-${o.id}`}>
          <button onClick={() => toggleOpen(o)} className="w-full p-4 flex items-center gap-4 text-left hover:bg-secondary/30">
            <div className="flex -space-x-3">
              {o.items.slice(0, 3).map((it, i) => (
                <img key={i} src={it.product_image ? fileUrl(it.product_image) : PLACEHOLDER} alt="" className="w-11 h-11 rounded-md object-cover border-2 border-white" onError={(e) => { e.currentTarget.src = PLACEHOLDER; }} />
              ))}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-bold text-sm">Pedido {o.order_number || o.id.slice(0, 8)}</p>
              <p className="text-xs text-muted-foreground">{(o.paid_at || o.created_at) ? new Date(o.paid_at || o.created_at).toLocaleDateString("es-MX", { day: "numeric", month: "long", year: "numeric" }) : ""} · {o.items.length} producto(s)</p>
              {claimByOrder[o.id]?.active && <span className="inline-flex items-center gap-1 mt-1 text-[10px] px-1.5 py-0.5 rounded font-semibold bg-amber-100 text-amber-700" data-testid={`order-claim-badge-${o.id}`}><Warning weight="bold" size={11} /> Reclamo abierto · {claimByOrder[o.id].folio}</span>}
            </div>
            <div className="text-right">
              <p className="font-bold">{formatPrice(o.total)}</p>
              <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${(ORDER_STATUS[o.status] || ORDER_STATUS.pending).cls}`}>{(ORDER_STATUS[o.status] || ORDER_STATUS.pending).label}</span>
            </div>
            <CaretDown weight="bold" size={16} className={`transition-transform ${open === o.id ? "rotate-180" : ""}`} />
          </button>
          {open === o.id && (
            <div className="border-t border-black/10 p-4 space-y-3">
              {(o.status === "pending_transfer" || o.status === "rejected") && (
                <div className={`rounded-lg p-3 flex items-center justify-between gap-3 ${o.status === "rejected" ? "bg-red-50 border border-red-200" : "bg-amber-50 border border-amber-200"}`}>
                  <p className="text-xs font-medium">{o.status === "rejected" ? `Comprobante rechazado: ${o.rejection_reason || ""}` : (o.receipt_image ? "Verificando tu transferencia…" : "Falta subir el comprobante de transferencia.")}</p>
                  {(o.status === "rejected" || !o.receipt_image) && <Link to={`/checkout/${o.id}`} data-testid={`upload-receipt-${o.id}`} className="nb-btn bg-primary text-primary-foreground px-3 py-1.5 text-xs whitespace-nowrap hover:brightness-110">Subir comprobante</Link>}
                </div>
              )}
              {o.receipt_image && (
                <a href={fileUrl(o.receipt_image)} target="_blank" rel="noreferrer" data-testid={`view-receipt-${o.id}`} className="text-xs text-primary hover:underline flex items-center gap-1"><Receipt weight="bold" size={13} /> Comprobante subido — ver</a>
              )}
              {o.status === "paid" && <TrackingTimeline order={o} onRefresh={() => syncTrack(o)} />}
              {o.items.map((it, i) => (
                <div key={i} className="flex items-center gap-3">
                  <img src={it.product_image ? fileUrl(it.product_image) : PLACEHOLDER} alt="" className="w-12 h-12 rounded object-cover" onError={(e) => { e.currentTarget.src = PLACEHOLDER; }} />
                  <Link to={`/producto/${it.product_id}`} className="flex-1 text-sm font-medium hover:text-primary">{it.product_title}{it.variant_name && <span className="text-muted-foreground"> · {it.variant_name}</span>}<span className="text-muted-foreground"> ×{it.quantity}</span></Link>
                  <span className="text-sm font-semibold">{formatPrice(it.subtotal)}</span>
                </div>
              ))}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-3 border-t border-black/10 text-sm">
                <div><p className="text-xs text-muted-foreground flex items-center gap-1"><CreditCard size={13} /> Forma de pago</p><p className="font-medium">{o.payment_method}</p></div>
                <div><p className="text-xs text-muted-foreground flex items-center gap-1"><Receipt size={13} /> Estado</p><p className="font-medium">{(ORDER_STATUS[o.status] || ORDER_STATUS.pending).label}</p></div>
                <div><p className="text-xs text-muted-foreground flex items-center gap-1"><Truck size={13} /> Envío</p><p className="font-medium">{o.shipping_status}</p></div>
                <div><p className="text-xs text-muted-foreground">Facturación</p><p className="font-medium">{o.billing?.tax_condition || "Consumidor final"}</p></div>
              </div>
              <div className="flex flex-wrap items-end justify-between gap-3 text-sm border-t border-black/10 pt-3">
                <div className="flex flex-wrap gap-2">
                  <button onClick={() => reorder(o)} data-testid={`reorder-${o.id}`} className="nb-btn bg-primary/10 text-primary px-4 py-2 flex items-center gap-1.5 hover:bg-primary/20 text-sm">
                    <ArrowClockwise weight="bold" size={15} /> Volver a comprar
                  </button>
                  {o.status === "paid" && (
                    claimByOrder[o.id]?.active ? (
                      <button onClick={() => goToClaims?.()} data-testid={`view-claim-${o.id}`} className="nb-btn bg-amber-100 text-amber-700 px-4 py-2 flex items-center gap-1.5 hover:brightness-95 text-sm">
                        <Warning weight="bold" size={15} /> Ver reclamo {claimByOrder[o.id].folio}
                      </button>
                    ) : (
                      <button onClick={() => setClaimOrder(o)} data-testid={`open-claim-${o.id}`} className="nb-btn bg-secondary text-foreground px-4 py-2 flex items-center gap-1.5 hover:bg-amber-100 hover:text-amber-700 text-sm">
                        <Warning weight="bold" size={15} /> Abrir reclamo
                      </button>
                    )
                  )}
                  {o.status === "paid" && (
                    !o.invoice ? (
                      <button onClick={() => setInvoiceOrder(o)} data-testid={`request-invoice-${o.id}`} className="nb-btn bg-secondary text-foreground px-4 py-2 flex items-center gap-1.5 hover:bg-primary/10 hover:text-primary text-sm">
                        <Receipt weight="bold" size={15} /> Solicitar factura
                      </button>
                    ) : o.invoice.status === "issued" ? (
                      <a href={fileUrl((o.invoice.files || [])[0]?.path)} target="_blank" rel="noreferrer" data-testid={`download-invoice-${o.id}`} className="nb-btn bg-[hsl(var(--meli-green))]/15 text-[hsl(var(--meli-green))] px-4 py-2 flex items-center gap-1.5 hover:brightness-95 text-sm">
                        <Receipt weight="bold" size={15} /> Descargar factura
                      </a>
                    ) : (
                      <span data-testid={`invoice-requested-${o.id}`} className="nb-btn bg-primary/10 text-primary px-4 py-2 flex items-center gap-1.5 text-sm cursor-default">
                        <Receipt weight="bold" size={15} /> Factura solicitada
                      </span>
                    )
                  )}
                </div>
                <div className="w-48 space-y-1">
                  <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span>{formatPrice(o.subtotal)}</span></div>
                  {o.discount > 0 && <div className="flex justify-between text-[hsl(var(--meli-green))]"><span>Descuento</span><span>-{formatPrice(o.discount)}</span></div>}
                  <div className="flex justify-between"><span className="text-muted-foreground">Envío</span><span>{o.shipping === 0 ? "Gratis" : formatPrice(o.shipping)}</span></div>
                  <div className="flex justify-between font-bold border-t border-black/10 pt-1"><span>Total</span><span>{formatPrice(o.total)}</span></div>
                </div>
              </div>
            </div>
          )}
        </div>
      ))}
      {claimOrder && <NewClaimModal order={claimOrder} onClose={() => setClaimOrder(null)} onCreated={() => { loadClaims(); toast.success("Revisa el estado en la pestaña “Mis reclamos”."); }} />}
      {invoiceOrder && <InvoiceModal order={invoiceOrder} savedFiscal={user?.fiscal} onClose={() => setInvoiceOrder(null)} onDone={loadOrders} />}
    </div>
  );
}

function QuestionsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { api.get("/buyer/questions").then((r) => setItems(r.data)).finally(() => setLoading(false)); }, []);
  if (loading) return <p className="text-muted-foreground">Cargando…</p>;
  if (items.length === 0) return <EmptyState icon={ChatCircleDots} title="Todavía no hiciste preguntas" cta={<Link to="/" className="mt-4 inline-block nb-btn bg-primary text-primary-foreground px-6 py-3 hover:brightness-110">Explorar productos</Link>} />;
  return (
    <div className="space-y-3" data-testid="my-questions-list">
      {items.map((q) => (
        <div key={q.id} className="bg-white rounded-lg nb-shadow p-4 flex gap-3" data-testid={`my-question-${q.id}`}>
          <img src={q.product_image ? fileUrl(q.product_image) : PLACEHOLDER} alt="" className="w-14 h-14 rounded object-cover" onError={(e) => { e.currentTarget.src = PLACEHOLDER; }} />
          <div className="flex-1 min-w-0">
            <Link to={`/producto/${q.product_id}`} className="text-sm font-semibold hover:text-primary">{q.product_title}</Link>
            <p className="text-sm mt-1">{q.text}</p>
            {q.answer ? (
              <div className="mt-1.5 pl-3 border-l-2 border-primary/30">
                <p className="text-sm text-foreground/80">{q.answer}</p>
                <p className="text-[11px] text-muted-foreground">Respuesta de {q.answered_by || "Todito Ventas"}{q.answered_at ? ` · ${new Date(q.answered_at).toLocaleDateString("es-MX")}` : ""}</p>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground italic mt-1">Sin responder aún</p>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function ReviewsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { api.get("/buyer/reviews").then((r) => setItems(r.data)).finally(() => setLoading(false)); }, []);
  if (loading) return <p className="text-muted-foreground">Cargando…</p>;
  if (items.length === 0) return <EmptyState icon={Star} title="Todavía no publicaste opiniones" cta={<Link to="/cuenta" onClick={() => {}} className="mt-4 inline-block text-sm text-primary hover:underline">Opina sobre tus compras</Link>} />;
  return (
    <div className="space-y-3" data-testid="my-reviews-list">
      {items.map((r) => (
        <div key={r.id} className="bg-white rounded-lg nb-shadow p-4 flex gap-3" data-testid={`my-review-${r.id}`}>
          <img src={r.product_image ? fileUrl(r.product_image) : PLACEHOLDER} alt="" className="w-14 h-14 rounded object-cover" onError={(e) => { e.currentTarget.src = PLACEHOLDER; }} />
          <div className="flex-1 min-w-0">
            <Link to={`/producto/${r.product_id}`} className="text-sm font-semibold hover:text-primary">{r.product_title}</Link>
            <div className="flex items-center gap-2 mt-1">
              <Stars value={r.rating} size={13} />
              {r.verified && <span className="text-[10px] font-bold text-[hsl(var(--meli-green))] flex items-center gap-0.5"><SealCheck weight="fill" size={12} /> Compra verificada</span>}
            </div>
            {r.comment && <p className="text-sm text-foreground/80 mt-1">{r.comment}</p>}
            {(r.photos || []).length > 0 && (
              <div className="flex gap-2 mt-2 flex-wrap">
                {r.photos.map((p, i) => <img key={i} src={fileUrl(p)} alt="" className="w-14 h-14 rounded object-cover border border-black/10" />)}
              </div>
            )}
            <p className="text-[11px] text-muted-foreground mt-1">{new Date(r.created_at).toLocaleDateString("es-MX")}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

function FavoritesTab() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { api.get("/favorites").then((r) => setProducts(r.data.products)).finally(() => setLoading(false)); }, []);
  if (loading) return <p className="text-muted-foreground">Cargando…</p>;
  if (products.length === 0) return <EmptyState icon={Heart} title="No tienes favoritos guardados" cta={<Link to="/" className="mt-4 inline-block nb-btn bg-primary text-primary-foreground px-6 py-3 hover:brightness-110">Explorar productos</Link>} />;
  return (
    <div>
      <div className="flex justify-end mb-3"><Link to="/favoritos" className="text-sm text-primary hover:underline">Ver todos →</Link></div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="account-favorites">
        {products.map((p) => <ProductCard key={p.id} product={p} />)}
      </div>
    </div>
  );
}

const emptyAddress = () => ({ label: "", recipient: "", phone: "", street: "", number: "", apartment: "", city: "", state: "", zip: "", notes: "", is_default: false });

function ProfileTab() {
  const { user, setUser } = useAuth();
  const [name, setName] = useState(user.name || "");
  const [phone, setPhone] = useState(user.phone || "");
  const [addresses, setAddresses] = useState(user.addresses || []);
  const [billing, setBilling] = useState(user.billing || { doc_type: "", doc_number: "", full_name: "", tax_condition: "Consumidor final", address: "" });
  const [saving, setSaving] = useState(false);

  const setAddr = (i, field, val) => setAddresses((arr) => arr.map((a, j) => j === i ? { ...a, [field]: val } : a));
  const setDefault = (i) => setAddresses((arr) => arr.map((a, j) => ({ ...a, is_default: j === i })));

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/auth/profile", { name, phone, addresses, billing });
      setUser(r.data);
      toast.success("Datos guardados");
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudo guardar"); }
    finally { setSaving(false); }
  };

  const inputCls = "w-full border border-black/10 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30";

  return (
    <div className="space-y-5" data-testid="profile-form">
      <div className="bg-white rounded-lg nb-shadow p-5">
        <h3 className="font-bold text-sm mb-3 flex items-center gap-2"><UserIcon weight="bold" size={17} /> Datos personales</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div><label className="text-xs text-muted-foreground">Nombre</label><input value={name} onChange={(e) => setName(e.target.value)} data-testid="profile-name" className={inputCls} /></div>
          <div><label className="text-xs text-muted-foreground">Teléfono</label><input value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="profile-phone" placeholder="Ej. +54 11 5555 5555" className={inputCls} /></div>
          <div className="sm:col-span-2"><label className="text-xs text-muted-foreground">Email</label><input value={user.email} disabled className={inputCls + " bg-secondary/40"} /></div>
        </div>
      </div>

      <div className="bg-white rounded-lg nb-shadow p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold text-sm flex items-center gap-2"><MapPin weight="bold" size={17} /> Direcciones de envío</h3>
          <button onClick={() => setAddresses((a) => [...a, { ...emptyAddress(), is_default: a.length === 0 }])} data-testid="add-address-button" className="text-sm text-primary flex items-center gap-1 hover:underline"><Plus weight="bold" size={14} /> Agregar</button>
        </div>
        {addresses.length === 0 && <p className="text-sm text-muted-foreground">No tienes direcciones guardadas.</p>}
        <div className="space-y-4">
          {addresses.map((a, i) => (
            <div key={i} className="border border-black/10 rounded-lg p-4" data-testid={`address-${i}`}>
              <div className="flex items-center justify-between mb-2">
                <input value={a.label} onChange={(e) => setAddr(i, "label", e.target.value)} placeholder="Etiqueta (Casa, Trabajo…)" className="text-sm font-semibold bg-transparent focus:outline-none" />
                <button onClick={() => setAddresses((arr) => arr.filter((_, j) => j !== i))} data-testid={`remove-address-${i}`} className="text-muted-foreground hover:text-red-500"><Trash weight="bold" size={16} /></button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input value={a.recipient} onChange={(e) => setAddr(i, "recipient", e.target.value)} placeholder="Destinatario" className={inputCls} />
                <input value={a.phone} onChange={(e) => setAddr(i, "phone", e.target.value)} placeholder="Teléfono" className={inputCls} />
                <input value={a.street} onChange={(e) => setAddr(i, "street", e.target.value)} placeholder="Calle" className={inputCls} />
                <input value={a.number} onChange={(e) => setAddr(i, "number", e.target.value)} placeholder="Número" className={inputCls} />
                <input value={a.apartment} onChange={(e) => setAddr(i, "apartment", e.target.value)} placeholder="Piso / Depto (opcional)" className={inputCls} />
                <input value={a.zip} onChange={(e) => setAddr(i, "zip", e.target.value)} placeholder="Código postal" className={inputCls} />
                <input value={a.city} onChange={(e) => setAddr(i, "city", e.target.value)} placeholder="Ciudad" className={inputCls} />
                <input value={a.state} onChange={(e) => setAddr(i, "state", e.target.value)} placeholder="Provincia" className={inputCls} />
              </div>
              <label className="flex items-center gap-2 mt-3 text-sm cursor-pointer">
                <input type="radio" name="default-addr" checked={!!a.is_default} onChange={() => setDefault(i)} data-testid={`default-address-${i}`} /> Usar como dirección predeterminada
              </label>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-lg nb-shadow p-5">
        <h3 className="font-bold text-sm mb-3 flex items-center gap-2"><Receipt weight="bold" size={17} /> Datos de facturación</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-muted-foreground">Condición fiscal</label>
            <select value={billing.tax_condition} onChange={(e) => setBilling((b) => ({ ...b, tax_condition: e.target.value }))} data-testid="billing-tax-condition" className={inputCls}>
              <option>Consumidor final</option>
              <option>Responsable inscripto</option>
              <option>Monotributista</option>
              <option>Exento</option>
            </select>
          </div>
          <div><label className="text-xs text-muted-foreground">Nombre / Razón social</label><input value={billing.full_name} onChange={(e) => setBilling((b) => ({ ...b, full_name: e.target.value }))} data-testid="billing-name" className={inputCls} /></div>
          <div>
            <label className="text-xs text-muted-foreground">Tipo de documento</label>
            <select value={billing.doc_type} onChange={(e) => setBilling((b) => ({ ...b, doc_type: e.target.value }))} data-testid="billing-doc-type" className={inputCls}>
              <option value="">—</option>
              <option value="DNI">DNI</option>
              <option value="CUIT">CUIT</option>
              <option value="CUIL">CUIL</option>
            </select>
          </div>
          <div><label className="text-xs text-muted-foreground">Número de documento</label><input value={billing.doc_number} onChange={(e) => setBilling((b) => ({ ...b, doc_number: e.target.value }))} data-testid="billing-doc-number" className={inputCls} /></div>
          <div className="sm:col-span-2"><label className="text-xs text-muted-foreground">Domicilio fiscal (opcional)</label><input value={billing.address} onChange={(e) => setBilling((b) => ({ ...b, address: e.target.value }))} data-testid="billing-address" className={inputCls} /></div>
        </div>
      </div>

      <div className="flex justify-end">
        <button onClick={save} disabled={saving} data-testid="save-profile-button" className="nb-btn bg-primary text-primary-foreground px-6 py-2.5 hover:brightness-110 disabled:opacity-50">{saving ? "Guardando…" : "Guardar cambios"}</button>
      </div>
    </div>
  );
}
